import { Activity } from 'lucide-react';

import { AddonsAddonListItem } from '@api/types.gen';

import { BadgeType } from '@@/Badge';
import {
  ResourceStatBlock,
  ResourceStatBlockStatus,
} from '@@/ResourceDetailHeader/ResourceStatBlock';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';

import { getStatusDescriptor } from '../AddonStatusBadge';

// StatusStatBlock renders the same effective status as AddonStatusBadge (lifecycle
// combined with health) in the header's bigger stat-block footprint.
export function StatusStatBlock({ addon }: { addon: AddonsAddonListItem }) {
  const { type, label, icon, summary, detail } = getStatusDescriptor(
    addon.lifecycleStatus,
    addon.lifecycleStatusMessage,
    addon.healthStatus,
    addon.healthMessage,
    addon.failedOperation
  );

  const value = (
    <span className="flex items-center gap-2">
      {icon}
      {label}
    </span>
  );

  const message =
    summary && detail ? (
      <>
        <div>{summary}</div>
        <div className="mt-1">{detail}</div>
      </>
    ) : (
      summary || detail
    );

  return (
    <ResourceStatBlock
      status={statBlockStatus(type)}
      data-cy="addon-status-stat"
    >
      <ResourceStatBlock.Label icon={<Activity className="h-3 w-3" />}>
        Status
      </ResourceStatBlock.Label>
      <ResourceStatBlock.Value align="start" size="base">
        {message ? (
          <TooltipWithChildren message={message}>
            <span>{value}</span>
          </TooltipWithChildren>
        ) : (
          value
        )}
      </ResourceStatBlock.Value>
    </ResourceStatBlock>
  );
}

// getStatusDescriptor speaks in badge types; map the ones it emits onto the
// stat block's colour vocabulary.
function statBlockStatus(type: BadgeType): ResourceStatBlockStatus {
  const statusByType: Partial<Record<BadgeType, ResourceStatBlockStatus>> = {
    success: 'success',
    danger: 'danger',
    warn: 'warning',
    info: 'pending',
  };

  return statusByType[type] ?? 'muted';
}
